(function () {
  'use strict';

  function resetReveal(panel) {
    if (!panel) return;
    panel.removeAttribute('data-reveal-state');
    panel.querySelectorAll('[data-reveal-item]').forEach(function (item) {
      item.removeAttribute('data-reveal-ready');
      item.removeAttribute('data-reveal-state');
      item.style.removeProperty('--reveal-calculated-delay');
    });
  }

  function reveal(panel, selector) {
    if (!panel || !window.Axiom || typeof window.Axiom.revealElements !== 'function') return;
    window.Axiom.revealElements(panel, {
      trigger: 'open',
      selector: selector,
      reset: true,
      stagger: 45
    });
  }

  class LocalizationSelector extends HTMLElement {
    connectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      var signal = this.abortController.signal;
      this.trigger = this.querySelector('[data-localization-choice-toggle]');
      this.panel = this.querySelector('[data-localization-choice-panel]');
      this.nativeSelect = this.querySelector('[data-localization-choice-native]');
      this.optionsHost = this.querySelector('[data-localization-choice-options]');
      this.optionTemplate = this.querySelector('[data-localization-choice-template]');
      this.optionsReady = Boolean(
        this.optionsHost && this.optionsHost.querySelector('[data-localization-choice-option]')
      );
      if (!this.trigger || !this.panel || !this.nativeSelect) return;

      this.trigger.addEventListener('click', this.handleTriggerClick.bind(this), { signal: signal });
      this.trigger.addEventListener('keydown', this.handleTriggerKeydown.bind(this), { signal: signal });
      this.panel.addEventListener('click', this.handlePanelClick.bind(this), { signal: signal });
      this.panel.addEventListener('keydown', this.handlePanelKeydown.bind(this), { signal: signal });
      this.nativeSelect.addEventListener('change', this.handleNativeChange.bind(this), { signal: signal });
      this.addEventListener('focusout', this.handleFocusout.bind(this), { signal: signal });
      document.addEventListener('pointerdown', this.handleDocumentPointerdown.bind(this), { signal: signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
    }

    get options() {
      return Array.prototype.slice.call(this.querySelectorAll('[data-localization-choice-option]'));
    }

    ensureOptions() {
      if (this.optionsReady || !this.optionsHost || !this.optionTemplate) return;

      var fragment = document.createDocumentFragment();
      Array.prototype.forEach.call(this.nativeSelect.options, function (nativeOption) {
        var optionFragment = this.optionTemplate.content.cloneNode(true);
        var option = optionFragment.querySelector('[data-localization-choice-option]');
        var label = optionFragment.querySelector('[data-localization-option-label]');
        var isActive = nativeOption.selected;

        if (!option || !label) return;
        option.dataset.localizationValue = nativeOption.value;
        option.dataset.localizationLabel = nativeOption.textContent.trim();
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        if (nativeOption.lang) option.lang = nativeOption.lang;
        label.textContent = nativeOption.textContent.trim();
        fragment.appendChild(optionFragment);
      }, this);

      this.optionsHost.appendChild(fragment);
      this.optionsReady = true;
    }

    close(restoreFocus) {
      if (!this.trigger || !this.panel) return;
      var wasOpen = !this.panel.hidden;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
      this.classList.remove('is-align-end');
      resetReveal(this.panel);
      if (restoreFocus && wasOpen) this.trigger.focus({ preventScroll: true });
    }

    open(focusPosition) {
      if (!this.trigger || !this.panel) return;
      this.ensureOptions();
      document.querySelectorAll('localization-selector').forEach(function (selector) {
        if (selector !== this && typeof selector.close === 'function') selector.close(false);
      }, this);

      this.classList.remove('is-align-end');
      this.panel.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      reveal(this.panel, '[data-reveal-item]');

      if (this.panel.getBoundingClientRect().right > window.innerWidth - 16) {
        this.classList.add('is-align-end');
      }

      if (focusPosition) {
        window.requestAnimationFrame(function () {
          var options = this.options;
          if (!options.length) return;
          var active = this.querySelector('[data-localization-choice-option].is-active');
          var target = focusPosition === 'last' ? options[options.length - 1] : active || options[0];
          target.focus({ preventScroll: true });
        }.bind(this));
      }
    }

    handleTriggerClick() {
      if (this.panel.hidden) this.open(false);
      else this.close(false);
    }

    handleTriggerKeydown(event) {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      this.open(event.key === 'ArrowUp' ? 'last' : 'active');
    }

    handlePanelClick(event) {
      var option = event.target.closest('[data-localization-choice-option]');
      if (!option || !this.panel.contains(option)) return;
      var nextValue = option.dataset.localizationValue;
      if (!nextValue || this.nativeSelect.value === nextValue) {
        this.close(true);
        return;
      }

      this.nativeSelect.value = nextValue;
      this.options.forEach(function (candidate) {
        var active = candidate === option;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      var current = this.querySelector('[data-localization-choice-current]');
      if (current) current.textContent = option.dataset.localizationLabel || option.textContent.trim();
      this.nativeSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    handlePanelKeydown(event) {
      var options = this.options;
      if (!options.length) return;
      var currentIndex = options.indexOf(document.activeElement);
      var nextIndex = currentIndex;
      if (event.key === 'ArrowDown') nextIndex = Math.min(options.length - 1, currentIndex + 1);
      else if (event.key === 'ArrowUp') nextIndex = Math.max(0, currentIndex - 1);
      else if (event.key === 'Home') nextIndex = 0;
      else if (event.key === 'End') nextIndex = options.length - 1;
      else if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      } else return;
      event.preventDefault();
      options[nextIndex < 0 ? 0 : nextIndex].focus({ preventScroll: true });
    }

    handleNativeChange() {
      if (!this.nativeSelect.form) return;
      if (typeof this.nativeSelect.form.requestSubmit === 'function') {
        this.nativeSelect.form.requestSubmit();
      } else {
        this.nativeSelect.form.submit();
      }
    }

    handleFocusout(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.close(false);
    }

    handleDocumentPointerdown(event) {
      if (!this.contains(event.target)) this.close(false);
    }
  }

  class LocalizationPicker extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-localization-picker-inline')) return;
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      var signal = this.abortController.signal;
      this.trigger = this.querySelector('[data-localization-toggle]');
      this.menu = this.querySelector('[data-localization-menu]');
      if (!this.trigger || !this.menu) return;

      this.trigger.addEventListener('click', this.handleTriggerClick.bind(this), { signal: signal });
      this.addEventListener('focusout', this.handleFocusout.bind(this), { signal: signal });
      document.addEventListener('pointerdown', this.handleDocumentPointerdown.bind(this), { signal: signal });
      document.addEventListener('keydown', this.handleDocumentKeydown.bind(this), { signal: signal });
    }

    disconnectedCallback() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
    }

    close(restoreFocus) {
      if (!this.trigger || !this.menu) return;
      var wasOpen = !this.menu.hidden;
      this.querySelectorAll('localization-selector').forEach(function (selector) {
        if (typeof selector.close === 'function') selector.close(false);
      });
      this.trigger.setAttribute('aria-expanded', 'false');
      this.menu.hidden = true;
      if (restoreFocus && wasOpen) this.trigger.focus({ preventScroll: true });
    }

    open() {
      if (!this.trigger || !this.menu) return;
      document.querySelectorAll('localization-picker:not([data-localization-picker-inline])').forEach(function (picker) {
        if (picker !== this && typeof picker.close === 'function') picker.close(false);
      }, this);
      this.menu.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      reveal(this.menu, '[data-localization-field-reveal]');
    }

    handleTriggerClick() {
      if (this.menu.hidden) this.open();
      else this.close(false);
    }

    handleFocusout(event) {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.close(false);
    }

    handleDocumentPointerdown(event) {
      if (!this.contains(event.target)) this.close(false);
    }

    handleDocumentKeydown(event) {
      if (event.key === 'Escape' && !this.menu.hidden) {
        event.preventDefault();
        this.close(true);
      }
    }
  }

  if (!customElements.get('localization-selector')) {
    customElements.define('localization-selector', LocalizationSelector);
  }
  if (!customElements.get('localization-picker')) {
    customElements.define('localization-picker', LocalizationPicker);
  }
})();
